import { Alert, Banner, Flex, Spinner, Text } from '@optiaxiom/react';

import { useConnections } from '@frontend/api/hooks';
import CampaignIcon from '@frontend/assets/Campaign.svg';
import CMPIcon from '@frontend/assets/CMP.svg';
import CMSIcon from '@frontend/assets/CMS.svg';
import CollaborationIcon from '@frontend/assets/Collaboration.svg';
import AnalyticsIcon from '@frontend/assets/CommerceAnalytics.svg';
import ConfiguredCommerce from '@frontend/assets/ConfiguredCommerce.svg';
import ContentRecommendationsIcon from '@frontend/assets/ContentRecommendations.svg';
import DataPlatformIcon from '@frontend/assets/DataPlatform.svg';
import ExperimentationIcon from '@frontend/assets/Experimentation.svg';
import PIMIcon from '@frontend/assets/PIM.svg';
import ProductRecsIcon from '@frontend/assets/ProductRecs.svg';
import { styles } from '@frontend/features/settings/Settings.css';
import { useOpalUser } from '@frontend/hooks';

import { ExtendedTurnstileInstance } from '../types';

import Empty from './Empty';
import { ProductInstances } from './ProductInstances';

// TODO: Use ID instead of name
const PRODUCTS = [
  {
    name: 'Analytics',
    icon: AnalyticsIcon,
  },
  {
    name: 'Campaign',
    icon: CampaignIcon,
  },
  {
    name: 'Collaboration',
    icon: CollaborationIcon,
  },
  {
    name: 'Configured Commerce',
    icon: ConfiguredCommerce,
  },
  {
    name: 'Content Management System',
    icon: CMSIcon,
  },
  {
    name: 'Optimizely Internal Content Management System',
    icon: CMSIcon,
  },
  {
    name: 'Content Marketing Platform',
    icon: CMPIcon,
  },
  {
    name: 'Content Recommendations',
    icon: ContentRecommendationsIcon,
  },
  {
    name: 'Data Platform',
    icon: DataPlatformIcon,
  },
  {
    name: 'Experimentation',
    icon: ExperimentationIcon,
  },
  {
    name: 'Product Information Management',
    icon: PIMIcon,
  },
  {
    name: 'Product Recommendation',
    icon: ProductRecsIcon,
  },
];

const PRODUCT_NAMES = PRODUCTS.map(p => p.name);

export function Instances() {
  const { opalUser } = useOpalUser();
  const { connectionGetter } = useConnections();

  if (!opalUser.instance.instance_id) {
    return <Alert intent="danger">Failed to fetch instance</Alert>;
  }

  if (connectionGetter.isPending || connectionGetter.isFetching) {
    return (
      <Flex justifyContent="center" alignItems="center" h="full">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (connectionGetter.error) {
    return (
      <Banner intent="danger">
        <Text>{connectionGetter.error.message}</Text>
      </Banner>
    );
  }

  const extendedInstancesByProduct = PRODUCTS.reduce(
    (acc, product) => {
      acc[product.name] = [];
      return acc;
    },
    {} as Record<string, ExtendedTurnstileInstance[]>
  );

  connectionGetter.connections?.forEach(connection => {
    const productName = connection.product_name;
    if (!PRODUCT_NAMES.includes(productName)) {
      return;
    }

    if (!extendedInstancesByProduct[productName]) {
      extendedInstancesByProduct[productName] = [];
    }

    extendedInstancesByProduct[productName].push({
      ...connection,
      disabled:
        connection.opal_instance_id !== null &&
        connection.opal_instance_id !== opalUser.instance.instance_id,
      selected: connection.opal_instance_id === opalUser.instance.instance_id,
    });
  });

  const hasInstancesToConnect = Object.values(extendedInstancesByProduct).some(instances =>
    Boolean(instances.length)
  );

  return (
    <Flex gap="12" className={styles.settingsContainer}>
      {!hasInstancesToConnect && (
        <Flex h="full" justifyContent="center" alignItems="center">
          <Empty />
        </Flex>
      )}
      {PRODUCTS.map(
        product =>
          extendedInstancesByProduct[product.name].length > 0 && (
            <ProductInstances
              key={product.name}
              productName={product.name}
              productIcon={product.icon}
              initialInstances={extendedInstancesByProduct[product.name].sort((a, b) => {
                const a_display_name = a.nickname ?? a.name;
                const b_display_name = b.nickname ?? b.name;
                return a_display_name.localeCompare(b_display_name);
              })}
            />
          )
      )}
    </Flex>
  );
}
